
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Tag, Copy, MoreHorizontal, Edit, Trash, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useScoutCoupons } from '@/hooks/useScoutCoupons';
import { useScoutMemberships } from '@/hooks/useScoutMemberships';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const ScoutCoupons = () => {
  const { profile } = useAuth();
  const { coupons, loading, createCoupon, deleteCoupon } = useScoutCoupons(profile?.id);
  const { memberships, loading: membershipsLoading } = useScoutMemberships(profile?.id);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 0,
    usage_limit: 1,
    valid_until: '',
    membership_id: ''
  });

  const handleCreateCoupon = async () => {
    if (!profile?.id || !newCoupon.code || !newCoupon.name || !newCoupon.discount_value || !newCoupon.membership_id) {
      toast.error('Please fill in all required fields including membership selection');
      return;
    }

    const success = await createCoupon({
      scout_id: profile.id,
      code: newCoupon.code.toUpperCase(),
      name: newCoupon.name,
      description: newCoupon.description,
      discount_type: newCoupon.discount_type,
      discount_value: newCoupon.discount_value,
      valid_from: new Date().toISOString(),
      valid_until: newCoupon.valid_until ? new Date(newCoupon.valid_until).toISOString() : undefined,
      usage_limit: newCoupon.usage_limit || undefined,
      is_active: true,
      membership_id: newCoupon.membership_id
    });

    if (success) {
      toast.success('Coupon created successfully!');
      setIsCreateDialogOpen(false);
      setNewCoupon({
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 0,
        usage_limit: 1,
        valid_until: '',
        membership_id: ''
      });
    } else {
      toast.error('Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    const success = await deleteCoupon(couponId);
    if (success) {
      toast.success('Coupon deleted successfully!');
    } else {
      toast.error('Failed to delete coupon');
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard!');
  };

  const getStatusColor = (coupon: any) => {
    if (!coupon.is_active) return 'destructive';
    if (coupon.valid_until && new Date(coupon.valid_until) < new Date()) return 'secondary';
    if (coupon.usage_limit && coupon.times_used >= coupon.usage_limit) return 'outline';
    return 'success';
  };

  const getStatusText = (coupon: any) => {
    if (!coupon.is_active) return 'Inactive';
    if (coupon.valid_until && new Date(coupon.valid_until) < new Date()) return 'Expired';
    if (coupon.usage_limit && coupon.times_used >= coupon.usage_limit) return 'Used Up';
    return 'Active';
  };

  const formatDiscount = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `$${value}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Coupons</h1>
          <p className="text-muted-foreground">
            Create and manage promotional codes for your athletes
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Coupon
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Coupon</DialogTitle>
              <DialogDescription>
                Create a promotional code for athletes or general use
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Coupon Name</Label>
                <Input 
                  id="name" 
                  placeholder="Descriptive name..." 
                  value={newCoupon.name}
                  onChange={(e) => setNewCoupon({...newCoupon, name: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="code">Coupon Code</Label>
                <Input 
                  id="code" 
                  placeholder="SCOUT-2024" 
                  className="font-mono" 
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="discountType">Discount Type</Label>
                  <Select 
                    value={newCoupon.discount_type} 
                    onValueChange={(value: 'percentage' | 'fixed') => setNewCoupon({...newCoupon, discount_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="discountValue">Discount Value</Label>
                  <Input 
                    id="discountValue" 
                    type="number" 
                    placeholder="25" 
                    value={newCoupon.discount_value || ''}
                    onChange={(e) => setNewCoupon({...newCoupon, discount_value: Number(e.target.value)})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="usageLimit">Usage Limit</Label>
                  <Input 
                    id="usageLimit" 
                    type="number" 
                    placeholder="1" 
                    value={newCoupon.usage_limit || ''}
                    onChange={(e) => setNewCoupon({...newCoupon, usage_limit: Number(e.target.value)})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expiresAt">Expires At</Label>
                  <Input 
                    id="expiresAt" 
                    type="date" 
                    value={newCoupon.valid_until}
                    onChange={(e) => setNewCoupon({...newCoupon, valid_until: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="membership">Applicable Membership *</Label>
                <Select 
                  value={newCoupon.membership_id} 
                  onValueChange={(value) => setNewCoupon({...newCoupon, membership_id: value})}
                  disabled={membershipsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={membershipsLoading ? "Loading memberships..." : "Select membership"} />
                  </SelectTrigger>
                  <SelectContent>
                    {memberships.map((membership) => (
                      <SelectItem key={membership.id} value={membership.id}>
                        {membership.name} (${(membership.price_cents / 100).toFixed(2)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {memberships.length === 0 && !membershipsLoading && (
                  <p className="text-sm text-muted-foreground">
                    No memberships assigned to you. Contact admin to assign memberships.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  placeholder="Describe the coupon..." 
                  rows={2}
                  value={newCoupon.description}
                  onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCoupon}>
                Create Coupon
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {coupons.map((coupon) => (
          <Card key={coupon.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Tag className="h-4 w-4 text-primary" />
                      <CardTitle className="text-lg">{coupon.name}</CardTitle>
                    </div>
                    <CardDescription className="text-sm">
                      {coupon.description || 'No description'}
                    </CardDescription>
                  </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Coupon
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      View Analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={() => handleDeleteCoupon(coupon.id)}
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete Coupon
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <code className="font-mono font-semibold">{coupon.code}</code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(coupon.code)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Discount</p>
                    <p className="font-semibold text-lg">
                      {formatDiscount(coupon.discount_type, coupon.discount_value)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Usage</p>
                    <p className="font-semibold">
                      {coupon.times_used}/{coupon.usage_limit || '∞'}
                    </p>
                  </div>
                </div>

                {(coupon as any).memberships && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Applicable Membership:</p>
                    <p className="font-medium">
                      {(coupon as any).memberships.name} (${((coupon as any).memberships.price_cents / 100).toFixed(2)})
                    </p>
                  </div>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span>{formatDate(coupon.created_at)}</span>
                  </div>
                  {coupon.valid_until && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expires:</span>
                      <span>{formatDate(coupon.valid_until)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Badge variant={getStatusColor(coupon)}>
                    {getStatusText(coupon)}
                  </Badge>
                  <Button variant="outline" size="sm">
                    Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {coupons.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No coupons created</h3>
            <p className="text-muted-foreground">
              Create your first promotional code to offer discounts to athletes
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScoutCoupons;
